import { useCallback, useRef, useState } from 'react';
import type React from 'react';

// Pinch, pan and double-tap zoom for the photo viewer (#85). The page itself
// can't zoom: the installed app sizes itself to the visual viewport
// (lib/viewport.ts), and zooming it would zoom the whole app, so the photo
// zooms inside its own box instead.

export const MAX_ZOOM = 4;
export const DOUBLE_TAP_ZOOM = 2.5;
const DOUBLE_TAP_MS = 300;

export interface Zoom {
  scale: number;
  x: number;
  y: number;
}

export const NO_ZOOM: Zoom = { scale: 1, x: 0, y: 0 };

/** Keeps the photo from being dragged further than its zoomed edges. */
export function clampZoom(zoom: Zoom, width: number, height: number): Zoom {
  const scale = Math.min(MAX_ZOOM, Math.max(1, zoom.scale));
  if (scale === 1) return NO_ZOOM;
  const maxX = ((scale - 1) * width) / 2;
  const maxY = ((scale - 1) * height) / 2;
  return {
    scale,
    x: Math.min(maxX, Math.max(-maxX, zoom.x)),
    y: Math.min(maxY, Math.max(-maxY, zoom.y)),
  };
}

/**
 * Zooms around `point` (relative to the box's centre), so what is under the
 * fingers stays under them.
 */
export function zoomAround(
  zoom: Zoom,
  scale: number,
  point: { x: number; y: number }
): Zoom {
  const ratio = scale / zoom.scale;
  return {
    scale,
    x: point.x - (point.x - zoom.x) * ratio,
    y: point.y - (point.y - zoom.y) * ratio,
  };
}

interface Gesture {
  startZoom: Zoom;
  startDistance: number;
  startMid: { x: number; y: number };
  lastSingle: { x: number; y: number } | null;
}

export function usePinchZoom() {
  const [zoom, setZoom] = useState<Zoom>(NO_ZOOM);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const gesture = useRef<Gesture | null>(null);
  const lastTap = useRef(0);
  /** A pinch or drag happened: the tap that ends it isn't a "close". */
  const moved = useRef(false);

  const reset = useCallback(() => {
    pointers.current.clear();
    gesture.current = null;
    setZoom(NO_ZOOM);
  }, []);

  const relative = (event: React.PointerEvent<HTMLElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return {
      x: event.clientX - (box.left + box.width / 2),
      y: event.clientY - (box.top + box.height / 2),
      width: box.width,
      height: box.height,
    };
  };

  const begin = () => {
    const points = [...pointers.current.values()];
    const [a, b] = points;
    gesture.current = {
      startZoom: zoomRef.current,
      startDistance: a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0,
      startMid: a && b ? { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } : { x: 0, y: 0 },
      lastSingle: points.length === 1 && a ? a : null,
    };
  };

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    const p = relative(event);
    pointers.current.set(event.pointerId, { x: p.x, y: p.y });
    if (pointers.current.size === 1) moved.current = false;
    begin();
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    const p = relative(event);
    pointers.current.set(event.pointerId, { x: p.x, y: p.y });
    const g = gesture.current;
    if (!g) return;
    const points = [...pointers.current.values()];
    if (points.length >= 2 && g.startDistance > 0) {
      const [a, b] = points as [{ x: number; y: number }, { x: number; y: number }];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const scale = g.startZoom.scale * (distance / g.startDistance);
      const next = zoomAround(g.startZoom, scale, g.startMid);
      moved.current = true;
      setZoom(
        clampZoom(
          { ...next, x: next.x + mid.x - g.startMid.x, y: next.y + mid.y - g.startMid.y },
          p.width,
          p.height
        )
      );
    } else if (points.length === 1 && g.lastSingle && zoomRef.current.scale > 1) {
      const dx = p.x - g.lastSingle.x;
      const dy = p.y - g.lastSingle.y;
      if (Math.abs(dx) + Math.abs(dy) > 2) moved.current = true;
      g.lastSingle = { x: p.x, y: p.y };
      const z = zoomRef.current;
      setZoom(clampZoom({ ...z, x: z.x + dx, y: z.y + dy }, p.width, p.height));
    }
  };

  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    const p = relative(event);
    const wasSingle = pointers.current.size === 1;
    pointers.current.delete(event.pointerId);
    begin();
    if (!wasSingle || moved.current) return;
    const now = Date.now();
    if (now - lastTap.current < DOUBLE_TAP_MS) {
      lastTap.current = 0;
      moved.current = true;
      const z = zoomRef.current;
      setZoom(
        z.scale > 1
          ? NO_ZOOM
          : clampZoom(zoomAround(z, DOUBLE_TAP_ZOOM, { x: p.x, y: p.y }), p.width, p.height)
      );
    } else {
      lastTap.current = now;
    }
  };

  const zoomed = zoom.scale > 1;

  return {
    zoom,
    zoomed,
    reset,
    /** True when the last touch was a pinch, a drag or a double tap. */
    gestured: () => moved.current,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onPointerCancel: onPointerUp,
    },
    style: {
      transform: `translate(${zoom.x}px, ${zoom.y}px) scale(${zoom.scale})`,
      transition: pointers.current.size ? 'none' : 'transform 0.15s ease-out',
      touchAction: 'none',
    } as React.CSSProperties,
  };
}
